import { onUnmounted, ref } from "vue"
import { useRouter } from "vue-router"
import { useAnalytics } from "./analytics"
import { useConcept } from "./concept"
import { useLocation } from "./locations"

export type TriggerType =
    | "reservation_dialog"
    | "large_party_section"
    | "private_dining_section"
    | "catering_section"
    | "direct"

export function useInquiryAnalytics() {
    const { logEvent, setUserProperties } = useAnalytics()
    const { concept } = useConcept()
    const { location } = useLocation()
    const router = useRouter()

    const formStartedAt = ref<number | null>(null)
    const storedTriggerType = ref<TriggerType | null>(null)
    const lastFieldFocused = ref<string>("")
    const hasStarted = ref(false)
    const hasSubmitted = ref(false)
    const isTracking = ref(false)
    const hasAbandoned = ref(false)

    const isClient = typeof window !== "undefined"

    function setContext() {
        if (!isClient) return
        const props = {
            concept: concept.value.name,
            location: location.value?.name || "unknown"
        }
        setUserProperties(props)
    }

    function trackFormOpened(triggerType: TriggerType) {
        if (!isClient) return
        storedTriggerType.value = triggerType
        setContext()
        const params = {
            trigger_type: triggerType,
            page_url: window.location.href
        }
        logEvent("form_opened", params)
    }

    function trackFormStarted() {
        if (!isClient || hasStarted.value) return
        hasStarted.value = true
        formStartedAt.value = Date.now()
        logEvent("form_started", {})
    }

    function trackFieldFocus(fieldName: string) {
        lastFieldFocused.value = fieldName
        if (!hasStarted.value) {
            trackFormStarted()
        }
    }

    function countFilledFields(formValues: Record<string, unknown>): number {
        return Object.values(formValues).filter(v =>
            v !== undefined && v !== null && v !== ""
        ).length
    }

    function getTimeBucket(seconds: number): string {
        if (seconds < 30) return "0-30s"
        if (seconds < 60) return "30-60s"
        if (seconds < 120) return "1-2min"
        if (seconds < 300) return "2-5min"
        if (seconds < 600) return "5-10min"
        return "10min+"
    }

    /**
     * Tracks form abandonment with trigger source for analytics filtering.
     * 
     * To filter false positives in Firebase Analytics:
     * Exclude events where abandon_trigger = "app_switch" AND session contains form_submitted
     */
    function trackFormAbandoned(formValues: Record<string, unknown>, trigger: "app_switch" | "modal_close" | "navigation" | "page_close" = "modal_close") {
        if (!isClient || !hasStarted.value || hasSubmitted.value || hasAbandoned.value) return
        hasAbandoned.value = true

        const timeInForm = formStartedAt.value
            ? Math.round((Date.now() - formStartedAt.value) / 1000)
            : 0

        const params = {
            last_field: lastFieldFocused.value,
            fields_completed: countFilledFields(formValues),
            time_in_form: getTimeBucket(timeInForm),
            abandon_trigger: trigger
        }
        logEvent("form_abandoned", params)
    }

    function trackFormSubmitted() {
        if (!isClient || hasSubmitted.value) return
        hasSubmitted.value = true

        const timeToComplete = formStartedAt.value
            ? Math.round((Date.now() - formStartedAt.value) / 1000)
            : 0

        const params = {
            time_to_complete: getTimeBucket(timeToComplete),
            trigger_type: storedTriggerType.value
        }
        logEvent("form_submitted", params)
        // Don't reset here - let the form close handler reset state
    }

    function reset() {
        formStartedAt.value = null
        storedTriggerType.value = null
        lastFieldFocused.value = ""
        hasStarted.value = false
        hasSubmitted.value = false
        hasAbandoned.value = false
    }

    let beforeUnloadHandler: ((e: BeforeUnloadEvent) => void) | null = null
    let routeGuardCleanup: (() => void) | null = null
    let visibilityHandler: (() => void) | null = null

    function setupAbandonmentTracking(getFormValues: () => Record<string, unknown>) {
        if (!isClient || isTracking.value) return
        isTracking.value = true

        beforeUnloadHandler = () => {
            if (hasStarted.value && !hasSubmitted.value) {
                trackFormAbandoned(getFormValues(), "page_close")
            }
        }
        window.addEventListener("beforeunload", beforeUnloadHandler)

        routeGuardCleanup = router.beforeEach((to, _from) => {
            if (hasStarted.value && !hasSubmitted.value) {
                trackFormAbandoned(getFormValues(), "navigation")
            }
            return to
        })

        visibilityHandler = () => {
            if (document.visibilityState === "hidden") {
                if (hasStarted.value && !hasSubmitted.value && !hasAbandoned.value) {
                    trackFormAbandoned(getFormValues(), "app_switch")
                }
            }
        }
        document.addEventListener("visibilitychange", visibilityHandler)
    }

    function cleanupAbandonmentTracking() {
        if (beforeUnloadHandler) {
            window.removeEventListener("beforeunload", beforeUnloadHandler)
            beforeUnloadHandler = null
        }
        if (routeGuardCleanup) {
            routeGuardCleanup()
            routeGuardCleanup = null
        }
        if (visibilityHandler) {
            document.removeEventListener("visibilitychange", visibilityHandler)
            visibilityHandler = null
        }
        isTracking.value = false
    }

    onUnmounted(() => {
        cleanupAbandonmentTracking()
    })

    return {
        trackFormOpened,
        trackFormStarted,
        trackFieldFocus,
        trackFormAbandoned,
        trackFormSubmitted,
        setupAbandonmentTracking,
        cleanupAbandonmentTracking,
        reset,
        hasStarted,
        hasSubmitted
    }
}
